import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Table } from 'react-bootstrap'
import {
  Button,
  FeatherLoader,
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import API from '../../utils/API'
import { DeleteConfirmationPrompt } from '../common'
import VideoCallingTab from './VideoCallingTab'

const ZOOM_CALLBACK_ERROR_MESSAGES = {
  zoom_callback_invalid: 'Something went wrong. Please try again.',
  zoom_state_invalid: 'That link expired. Please connect again.',
  zoom_token_exchange_failed: 'Could not complete the connection.',
  zoom_permissions_denied: 'Please accept all permissions and try again.',
  zoom_user_fetch_failed: 'Could not read your Zoom account.',
  validation_error: 'Something went wrong. Please try again.',
  zoom_not_configured: 'Zoom is not configured. Please contact support.',
}

const getZoomCallbackParams = (search) => {
  const searchParams = new URLSearchParams(search)
  return {
    error: searchParams.get('error'),
    code: searchParams.get('code'),
    state: searchParams.get('state'),
  }
}

const ZoomTab = () => {
  const { user } = useUserService()
  const userId = user?._id
  const location = useLocation()
  const history = useHistory()
  const Error = useErrorService()
  const Notification = useNotificationService()

  const [zoomAccount, setZoomAccount] = useState(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)

  // Prevents a second GET after OAuth finishes and query params are cleared
  const oauthHandledRef = useRef(false)
  const hasLoadedAccountRef = useRef(false)

  const clearZoomQueryParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.delete('code')
    searchParams.delete('state')
    searchParams.delete('error')
    const nextSearch = searchParams.toString()
    history.replace(
      nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname,
    )
  }, [history, location.pathname, location.search])

  const showZoomCallbackError = useCallback(
    (code) => {
      const message =
        ZOOM_CALLBACK_ERROR_MESSAGES[code] ||
        'Something went wrong. Please try again.'
      Notification.showNotification(message, 'danger')
    },
    [Notification],
  )

  const fetchZoomAccount = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data } = await API.AdminAPI.zoom.getAccount()
      setZoomAccount(data?.email ? { email: data.email } : null)
    } catch (e) {
      if (e?.code === 'zoom_not_connected') {
        setZoomAccount(null)
      } else {
        Error.showError(e)
      }
    } finally {
      setIsLoading(false)
      hasLoadedAccountRef.current = true
    }
  }, [Error])

  useEffect(() => {
    if (!userId) return

    const { error, code, state } = getZoomCallbackParams(location.search)
    const hasOAuthParams = Boolean(error || code || state)

    // After OAuth we clear ?code&state — do not treat that as a fresh page load
    if (!hasOAuthParams && oauthHandledRef.current) {
      return
    }

    // Already loaded for a normal visit; avoid duplicate GETs from effect churn
    if (!hasOAuthParams && hasLoadedAccountRef.current) {
      return
    }

    let cancelled = false

    const run = async () => {
      if (hasOAuthParams) {
        if (oauthHandledRef.current) return
        oauthHandledRef.current = true
        setIsLoading(true)

        if (error) {
          if (!cancelled) {
            Notification.showNotification(
              'Zoom connection was cancelled.',
              'danger',
            )
            setIsLoading(false)
            clearZoomQueryParams()
          }
          return
        }

        if (!code || !state) {
          if (!cancelled) {
            showZoomCallbackError('zoom_callback_invalid')
            setIsLoading(false)
            clearZoomQueryParams()
          }
          return
        }

        try {
          setIsConnecting(true)
          await API.AdminAPI.zoom.authenticateCallback(code, state)
          if (cancelled) return

          // Keep the same loading state through connect → GET (no empty flash)
          const { data } = await API.AdminAPI.zoom.getAccount()
          if (cancelled) return

          setZoomAccount(data?.email ? { email: data.email } : null)
          Notification.showNotification(
            'Zoom account connected successfully.',
            'success',
          )
        } catch (e) {
          if (cancelled) return
          if (e?.code && ZOOM_CALLBACK_ERROR_MESSAGES[e.code]) {
            showZoomCallbackError(e.code)
          } else {
            Error.showError(e)
          }
        } finally {
          if (!cancelled) {
            setIsConnecting(false)
            setIsLoading(false)
            hasLoadedAccountRef.current = true
            clearZoomQueryParams()
          }
        }
        return
      }

      await fetchZoomAccount()
    }

    run()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, location.search])

  const handleConnectZoom = () => {
    if (isConnecting || isLoading || zoomAccount) return
    setIsConnecting(true)
    window.location.href = `${process.env.REACT_APP_API_ENDPOINT}/zoom/authenticate`
  }

  const confirmDisconnectZoom = async () => {
    setIsDisconnecting(true)
    try {
      const { data } = await API.AdminAPI.zoom.disconnectZoom()
      if (data?.status) {
        Notification.showNotification(
          'Zoom account disconnected successfully.',
          'success',
        )
        setShowDisconnectModal(false)
        await fetchZoomAccount()
      }
    } catch (e) {
      if (e?.code === 'zoom_not_connected') {
        Notification.showNotification(
          e?.message || 'This coach has not connected a Zoom account yet.',
          'danger',
        )
        setShowDisconnectModal(false)
        await fetchZoomAccount()
      } else {
        Error.showError(e)
      }
    } finally {
      setIsDisconnecting(false)
    }
  }

  return (
    <>
      <DeleteConfirmationPrompt
        showModal={showDisconnectModal}
        setShowModal={setShowDisconnectModal}
        isDeleteLoading={isDisconnecting}
        handleDelete={confirmDisconnectZoom}
        message='Your existing sessions will keep their meeting links, but you will no longer join them as host. New sessions will not have Zoom links until you reconnect.'
      />

      <section>
        <div className='d-flex justify-content-between align-items-center'>
          <h3 className='my-auto s3'>Your Zoom Account</h3>
          <Button
            variant='gray'
            size='md'
            className='line-height-normal text-uppercase border-radius-3'
            onClick={handleConnectZoom}
            disabled={isConnecting || isLoading || Boolean(zoomAccount)}>
            <span className='s8'>
              {isConnecting ? 'CONNECTING...' : 'CONNECT'}
            </span>
          </Button>
        </div>

        <p className='s6 text-muted mt-2 mb-0'>
          Free Zoom plans have a 40-minute meeting limit. Please accept all
          permissions when connecting.
        </p>

        <Table responsive className='custom-table mt-3 mb-0'>
          <thead>
            <tr>
              <th className='text-uppercase sa2'>#</th>
              <th className='text-uppercase sa2'>Email</th>
              <th className='text-uppercase sa2'>Actions</th>
            </tr>
          </thead>
          {isLoading && (
            <tr>
              <td className='text-center border-0 pt-3' colSpan='3'>
                <FeatherLoader />
              </td>
            </tr>
          )}
          {!zoomAccount && !isLoading && (
            <tr>
              <td className='text-center s6 py-3' colSpan='3'>
                No accounts connected yet.
              </td>
            </tr>
          )}
          <tbody>
            {zoomAccount && !isLoading && (
              <tr>
                <td>1</td>
                <td className='text-break'>
                  <span>{zoomAccount.email}</span>
                </td>
                <td
                  className='text-danger cursor-pointer'
                  onClick={() => setShowDisconnectModal(true)}>
                  Disconnect
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </section>

      <section className='mt-5'>
        <VideoCallingTab isZoomConnected={Boolean(zoomAccount)} />
      </section>
    </>
  )
}

export default ZoomTab
